import { useEffect, useMemo, useState } from 'react';
import { Search, X, Check } from 'lucide-react';
import { useModalKeys } from '../../../hooks/useModalKeys';

/**
 * Picker — modal de seleção com busca opcional.
 * Porte do apps/prospect-web/src/components/Picker.tsx.
 */

export interface PickerOption {
  value: string | number;
  label: string;
  sublabel?: string;
}

interface Props {
  open: boolean;
  title: string;
  options: PickerOption[];
  selected?: string | number;
  searchable?: boolean;
  onSelect: (opt: PickerOption) => void;
  onClose: () => void;
}

export default function Picker({
  open,
  title,
  options,
  selected,
  searchable,
  onSelect,
  onClose,
}: Props) {
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) setSearch('');
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || o.sublabel?.toLowerCase().includes(q),
    );
  }, [options, search]);

  // Esc fecha só o picker (topo da pilha); clique fora NÃO fecha (Fase 30)
  useModalKeys(onClose, undefined, open);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4">
      <div className="bg-surface rounded-2xl border border-line w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <h2 className="font-bold text-fg">{title}</h2>
          <button onClick={onClose} className="text-fg-2 hover:text-fg">
            <X size={20} />
          </button>
        </div>

        {searchable && (
          <div className="p-3 border-b border-line">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"
              />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar..."
                className="w-full bg-canvas border border-line rounded-xl pl-9 pr-3 h-10 text-fg text-sm outline-none focus:border-primary"
              />
            </div>
          </div>
        )}

        <ul className="overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <li className="px-4 py-10 text-center text-muted text-sm">
              Nenhum resultado
            </li>
          ) : (
            filtered.map((o) => {
              const isSel = selected === o.value;
              return (
                <li key={String(o.value)}>
                  <button
                    onClick={() => {
                      onSelect(o);
                      onClose();
                    }}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-canvas border-b border-line/60 ${
                      isSel ? 'bg-primary-soft/30' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-fg truncate">{o.label}</p>
                      {o.sublabel && (
                        <p className="text-xs text-muted truncate">{o.sublabel}</p>
                      )}
                    </div>
                    {isSel && (
                      <Check size={16} className="text-primary flex-shrink-0" />
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
}
